import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import { SystemAdminService, SystemAdminBusiness } from '../../../services/system-admin/system-admin.service';
import { AuthService } from '../../../services/auth';
import { ToastService } from '../../../shared/services/toast.service';
import { DataTable, TableColumn } from '../../../shared/components/data-table/data-table';
import { TableCellDef } from '../../../shared/directives/table-cell.directive';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal';
import { BusinessActionsComponent } from './business-actions/business-actions';

@Component({
  selector: 'app-admin-businesses',
  imports: [CommonModule, FormsModule, DataTable, TableCellDef, ConfirmModal, BusinessActionsComponent],
  templateUrl: './businesses.html',
})
export class AdminBusinesses implements OnInit {
  private systemAdminService = inject(SystemAdminService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);
  private search$ = new Subject<string>();

  businesses = signal<SystemAdminBusiness[]>([]);
  loading = signal(false);
  page = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 1;
  searchTerm = '';

  // Filters
  isActiveFilter: string = 'all';
  membershipStatusFilter: string = 'all';

  // Action modals
  showConfirmModal = signal(false);
  confirmModalTitle = '';
  confirmModalSubtitle = '';
  confirmModalVariant: 'danger' | 'warning' | 'primary' = 'warning';
  confirmModalAction: (() => void) | null = null;
  confirmLoading = signal(false);

  // Business actions panel
  selectedBusiness = signal<SystemAdminBusiness | null>(null);
  showActionsPanel = signal(false);

  constructor() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          this.searchTerm = q;
          this.page = 1;
          this.loadBusinesses();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadBusinesses();
  }

  onSearch = (q: string) => this.search$.next(q);

  loadBusinesses(): void {
    this.loading.set(true);
    const isActive = this.isActiveFilter === 'all' ? undefined : this.isActiveFilter === 'true';
    const membershipStatus = this.membershipStatusFilter === 'all' ? undefined : this.membershipStatusFilter;

    this.systemAdminService.getBusinessesList({
      page: this.page,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm || undefined,
      isActive,
      membershipStatus: membershipStatus || undefined,
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.businesses.set(response.businesses);
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.loading.set(false);
      },
      error: () => {
        this.toast.show('Error al cargar negocios', 'error');
        this.loading.set(false);
      }
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadBusinesses();
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadBusinesses();
  }

  // Actions
  onOpenActions(business: SystemAdminBusiness): void {
    this.selectedBusiness.set(business);
    this.showActionsPanel.set(true);
  }

  onCloseActions(): void {
    this.showActionsPanel.set(false);
    this.selectedBusiness.set(null);
  }

  onImpersonate(business: SystemAdminBusiness): void {
    this.confirmModalTitle = 'Impersonar negocio';
    this.confirmModalSubtitle = `¿Deseas acceder como el negocio "${business.name}"? Podrás ver y gestionar como si fueras el propietario.`;
    this.confirmModalVariant = 'primary';
    this.confirmModalAction = () => this.executeImpersonate(business);
    this.showConfirmModal.set(true);
  }

  onToggleActive(business: SystemAdminBusiness): void {
    const action = business.isActive ? 'desactivar' : 'activar';
    this.confirmModalTitle = `${business.isActive ? 'Desactivar' : 'Activar'} negocio`;
    this.confirmModalSubtitle = `¿Estás seguro de ${action} el negocio "${business.name}"?${business.isActive ? ' Los usuarios no podrán acceder hasta que se reactive.' : ''}`;
    this.confirmModalVariant = business.isActive ? 'danger' : 'primary';
    this.confirmModalAction = () => this.executeToggleActive(business);
    this.showConfirmModal.set(true);
  }

  onConfirmAction(): void {
    if (this.confirmModalAction) {
      this.confirmModalAction();
    }
  }

  onCancelAction(): void {
    this.showConfirmModal.set(false);
    this.confirmModalAction = null;
  }

  private executeImpersonate(business: SystemAdminBusiness): void {
    this.confirmLoading.set(true);
    this.systemAdminService.impersonateBusiness(business.businessId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        const authData = JSON.parse(localStorage.getItem('auth_data') || '{}');
        authData.token = response.token;
        authData.expiresAt = response.expiresAt;
        authData.business_name = response.businessName;
        authData.business_id = response.businessId;
        authData.business_is_active = true;
        localStorage.setItem('auth_data', JSON.stringify(authData));
        this.authService.login(authData);
        this.authService.loadFromStorage();
        this.confirmLoading.set(false);
        this.showConfirmModal.set(false);
        this.router.navigate(['/agendas']);
      },
      error: () => {
        this.toast.show('Error al impersonar el negocio', 'error');
        this.confirmLoading.set(false);
        this.showConfirmModal.set(false);
      }
    });
  }

  private executeToggleActive(business: SystemAdminBusiness): void {
    this.confirmLoading.set(true);
    const action$ = business.isActive
      ? this.systemAdminService.deactivateBusiness(business.businessId)
      : this.systemAdminService.activateBusiness(business.businessId);

    action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toast.show(
          `Negocio ${business.isActive ? 'desactivado' : 'activado'} exitosamente`,
          'success'
        );
        this.confirmLoading.set(false);
        this.showConfirmModal.set(false);
        this.loadBusinesses();
      },
      error: () => {
        this.toast.show('Error al cambiar estado del negocio', 'error');
        this.confirmLoading.set(false);
        this.showConfirmModal.set(false);
      }
    });
  }

  onActionCompleted(): void {
    this.showActionsPanel.set(false);
    this.selectedBusiness.set(null);
    this.loadBusinesses();
  }

  // Helpers
  getStatusBadgeClass(isActive: boolean): string {
    return isActive
      ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  }

  getMembershipBadgeClass(status: string): string {
    const s = status?.toLowerCase() || '';
    if (s.includes('activa') && !s.includes('vencida')) return 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400';
    if (s.includes('prueba') || s.includes('trial')) return 'bg-warning-50 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400';
    if (s.includes('vencida') || s.includes('expired')) return 'bg-error-50 text-error-700 dark:bg-error-500/10 dark:text-error-400';
    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return formatDate(date, 'dd/MM/yyyy', 'es-CO');
  }

  columns: TableColumn[] = [
    { header: 'Negocio', cell: 'business_name' },
    { header: 'Estado', cell: 'status_badge', widthClass: 'w-28' },
    { header: 'Membresía', cell: 'membership_badge', widthClass: 'w-36' },
    { header: 'Plan', field: 'membershipPlanName', format: (v) => v || 'Sin plan' },
    { header: 'Creación', field: 'createdAt', format: (v) => formatDate(v, 'dd/MM/yyyy', 'es-CO') },
    { header: 'Vencimiento', cell: 'expiration', widthClass: 'w-28' },
    { header: '', cell: 'actions', widthClass: 'w-32' },
  ];
}
